import { useEffect, useState } from "react";
import { Button, Col, Form, InputGroup, Modal, Pagination, Row, Table } from "react-bootstrap";
import { FaChevronDown } from "react-icons/fa";
import { useSelector } from "react-redux";
import useDebouncedSearch from "../hooks/useDebouncedSearch";
import { AdminInstance } from "../utils/adminClass";
import { FormatInstance } from "../utils/formatClass";
import { SearchBusinessInstance } from "../utils/searchBusinessClass";
import AdminUserBusinessList from "./adminUserBusinessList";

const columns = [
  { header: "Name", propName: "name" },
  { header: "Brand", propName: "brand.name" },
  { header: "Category", propName: "category.name" },
  // Add other columns as needed
];

const actions = [
  {
    name: "Edit",
    method: (item: any) => {
      /* handle edit */
    },
  },
  {
    name: "Delete",
    method: (item: any) => {
      /* handle delete */
    },
  },
];

const ITEMS_PER_PAGE = 10;

export const AdminUserModal = ({
  isModalOpen,
  onClose,
  user,
  setSelectedUser,
}: {
  isModalOpen: boolean;
  onClose: () => void;
  user: any;
  setSelectedUser: (user: any) => void;
}) => {
  const { searchResults } = useSelector((state: any) => state.searchBusiness);
  const { username, email, seeds, uid } = user;
  const [seedValue, setSeedValue] = useState(0);
  const [description, setDescription] = useState("");
  const [seedLedger, setSeedLedger] = useState<any[]>([]);
  const [userBusinesses, setUserBusinesses] = useState<any[]>([]);
  const [showBanConfirmation, setShowBanConfirmation] = useState(false);
  const [confirmationMessage, setConfirmationMessage] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [isChallenge, setIsChallenge] = useState(false);
  const { searchInput, debouncedSearchTerm, setSearchInput } = useDebouncedSearch();

  useEffect(() => {
    // fetch user seed ledger
    (async () => {
      const { seedLedger } = await AdminInstance.getUserSeedLedger(user.id);
      console.log("seed", seedLedger);
      setSeedLedger(seedLedger);
    })();

    (async () => {
      const res = await AdminInstance.getUserBusinesses(user.id);
      setUserBusinesses(res);
    })();
  }, []);

  useEffect(() => {
    if (debouncedSearchTerm) {
      SearchBusinessInstance.setSearchQuery(debouncedSearchTerm);
    } else {
      SearchBusinessInstance.setSearchQuery("");
    }
  }, [debouncedSearchTerm]);

  const handleSearchQueryChange = (event: any) => {
    setSearchInput(event.target.value);
  };

  // handler for adding or removing seeds
  const handleSeeds = async (method: string) => {
    const { user: updatedUser } = await AdminInstance.manageUserSeeds({
      user,
      seeds: seedValue,
      method,
      description,
      isChallenge,
    });

    setSelectedUser(updatedUser);
    setDescription("");
    setSeedValue(0);
  };

  // handler for removing a user from a business
  const handleRemoveBusiness = async (object: any) => {
    await AdminInstance.removeUserFromBusiness({
      user,
      businessId: object.businessId,
    });

    setUserBusinesses(userBusinesses.filter((business) => business.businessId !== object.businessId));
  };

  // handler for adding a user to a business
  const handleAddBusiness = async ({
    businessId,
    businessType,
  }: {
    businessId: string;
    businessType: "brand" | "venue";
  }) => {
    await AdminInstance.addUserToBusiness({
      user,
      businessId,
      businessType,
    });
  };

  const handleToggleBan = async () => {
    const updatedUser = await AdminInstance.toggleBanUser(user.id);
    setShowBanConfirmation(false);

    setSelectedUser(updatedUser);

    // const { user: updatedUser } = await AdminInstance.getUserDetails(user.id);
    // setSelectedUser(updatedUser);
  };

  const openBanConfirmation = () => {
    if (user.isBanned) {
      setConfirmationMessage("Are you sure you want to unban this user?");
    } else {
      setConfirmationMessage("Are you sure you want to ban this user?");
    }
    setShowBanConfirmation(true);
  };

  const paginatedSeedLedger = seedLedger.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  const totalPages = Math.ceil(seedLedger.length / ITEMS_PER_PAGE);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  return (
    <>
      <Modal show={isModalOpen} onHide={onClose} size="lg" centered backdrop="static" keyboard={false}>
        <Modal.Header closeButton>
          <Modal.Title>User Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row>
            <Col md={6}>
              <Form.Group as={Row} className="mb-3">
                <Form.Label column sm={4}>
                  Name:
                </Form.Label>
                <Col sm={8}>
                  <Form.Control plaintext readOnly value={username} />
                </Col>
              </Form.Group>
              <Form.Group as={Row} className="mb-3">
                <Form.Label column sm={4}>
                  User ID:
                </Form.Label>
                <Col sm={8}>
                  <Form.Control plaintext readOnly value={user.id} />
                </Col>
              </Form.Group>
              <Form.Group as={Row} className="mb-3">
                <Form.Label column sm={4}>
                  Email:
                </Form.Label>
                <Col sm={8}>
                  <Form.Control plaintext readOnly value={email} />
                </Col>
              </Form.Group>
              <Form.Group as={Row} className="mb-3">
                <Form.Label column sm={4}>
                  First Name:
                </Form.Label>
                <Col sm={8}>
                  <Form.Control plaintext readOnly value={user.firstName} />
                </Col>
              </Form.Group>

              <Form.Group as={Row} className="mb-3">
                <Form.Label column sm={4}>
                  Seed Balance:
                </Form.Label>
                <Col sm={8}>
                  <Form.Control plaintext readOnly value={seeds} />
                </Col>
              </Form.Group>
            </Col>
            <Col md={6}>
              <h5>Manage Seeds</h5>
              <InputGroup className="mb-3">
                <Form.Control
                  type="number"
                  placeholder="Enter seeds"
                  value={seedValue}
                  onChange={(e) => setSeedValue(parseInt(e.target.value))}
                />
              </InputGroup>
              <InputGroup className="mb-3">
                <Form.Control
                  type="text"
                  placeholder="Description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </InputGroup>
              {/* is challenge toggle button */}
              <Row className="mt-4">
                <Col>
                  <Form.Check
                    type="switch"
                    id="isChallenge"
                    label="Is Challenge"
                    checked={isChallenge}
                    onChange={(e) => setIsChallenge(e.target.checked)}
                  />
                </Col>
              </Row>
              <Button
                variant="success"
                className="me-2"
                disabled={seedValue === 0 || description === ""}
                onClick={() => handleSeeds("add")}
              >
                Add Seeds
              </Button>
              <Button
                variant="danger"
                disabled={seedValue === 0 || description === ""}
                onClick={() => handleSeeds("remove")}
              >
                Deduct Seeds
              </Button>
            </Col>
          </Row>

          <h5 className="mt-4">Manage Businesses</h5>
          {userBusinesses.length > 0 ? (
            <AdminUserBusinessList
              businesses={userBusinesses}
              onSelect={() => {}}
              action="remove"
              handler={handleRemoveBusiness}
            />
          ) : (
            <p>No businesses found</p>
          )}

          <h5 className="mt-4">Add New Business</h5>
          <InputGroup className="mb-3">
            <Form.Control
              type="text"
              placeholder="Search for businesses"
              value={searchInput}
              onChange={handleSearchQueryChange}
            />
          </InputGroup>
          {searchResults.length > 0 ? (
            <AdminUserBusinessList
              businesses={searchResults}
              onSelect={() => {}}
              action="add"
              handler={handleAddBusiness}
            />
          ) : (
            <p>No businesses found</p>
          )}

          <h5 className="mt-4">View Seed Ledger</h5>
          <InputGroup className="mb-3 align-items-center">
            <FaChevronDown className="me-2" />
            <span>View All</span>
          </InputGroup>
          {seedLedger.length > 0 ? (
            <>
              <Table striped bordered hover>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Seeds</th>
                    <th>Description</th>
                    <th>Item</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedSeedLedger.map((item, index) => (
                    <tr key={index}>
                      <td>{FormatInstance.renderReadableExpirationTimestamp(item.createdAt)}</td>
                      <td>{(item.type === "earn" ? "+" : "-") + item.amount}</td>
                      <td>{item.itemType === "experience" ? "Purchase Experience" : item.description}</td>
                      <td>
                        {item.itemType === "image"
                          ? item[item.itemType].key.split("/").slice(0, 2).join("/")
                          : item.itemType === "experience"
                            ? item[item.itemType].headline
                            : item[item.itemType]?.name || "No Item Found"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              <Pagination>
                <Pagination.First onClick={() => handlePageChange(1)} disabled={currentPage === 1} />
                <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />
                {[...Array(totalPages).keys()].map((number) => (
                  <Pagination.Item
                    key={number + 1}
                    active={number + 1 === currentPage}
                    onClick={() => handlePageChange(number + 1)}
                  >
                    {number + 1}
                  </Pagination.Item>
                ))}
                <Pagination.Next
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                />
                <Pagination.Last onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages} />
              </Pagination>
            </>
          ) : (
            <p>No seed ledger found</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant={user.isBanned ? "warning" : "danger"} onClick={openBanConfirmation}>
            {user.isBanned ? "Unban User" : "Ban User"}
          </Button>
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Confirmation Modal */}
      <Modal
        show={showBanConfirmation}
        onHide={() => setShowBanConfirmation(false)}
        centered
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirmation</Modal.Title>
        </Modal.Header>
        <Modal.Body>{confirmationMessage}</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowBanConfirmation(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleToggleBan}>
            Confirm
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};
